import * as Sentry from "@sentry/react";
import { sanitizeError, redactSensitiveFields, redactString } from "./sentry-sanitization";

/**
 * Check if running in local/development environment
 * @returns {boolean} True if running locally
 */
const isLocalOrDevelopment = () => {
    if (typeof window === 'undefined') return false;
    
    return (
        process.env.NODE_ENV === 'development' || 
        window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('localhost')
    );
};

/**
 * Common Sentry logger utility - Only logs errors to Sentry
 * Will not log anything if running in local/development environment
 * @param {Error|string} error - Error object or error message
 * @param {Object} options - Logging options
 * @param {string} options.service - Service name (e.g., 'ai-chat', 'api')
 * @param {string} options.event - Event type (e.g., 'api_error', 'polling_timeout')
 * @param {string} options.pageType - Page type (optional)
 * @param {Object} options.extra - Additional context data
 * @param {Object} options.tags - Additional tags
 */
export const logErrorToSentry = (error, options = {}) => {
    // Don't log to Sentry if running locally
    if (isLocalOrDevelopment()) {
        return;
    }

    const {
        service = 'unknown',
        event = 'error',
        pageType = null,
        extra = {},
        tags = {}
    } = options;

    // Build tags object (sanitize to prevent sensitive data in tags)
    const sentryTags = {
        service,
        event,
        ...(pageType && { pageType }),
        ...redactSensitiveFields(tags)
    };

    // Build extra data (sanitize to prevent PII/PHI leakage)
    const sentryExtra = redactSensitiveFields(extra);

    // If error is an Error object, sanitize it before sending
    if (error instanceof Error) {
        const sanitizedError = sanitizeError(error);
        Sentry.captureException(sanitizedError, {
            tags: sentryTags,
            extra: sentryExtra
        });
    } else {
        // If error is a string, sanitize it before sending
        const sanitizedMessage = redactString(String(error));
        Sentry.captureMessage(sanitizedMessage, 'error', {
            tags: sentryTags,
            extra: sentryExtra
        });
    }
};

/**
 * Log error with exception (for Error objects)
 * @param {Error} error - Error object
 * @param {Object} options - Logging options
 */
export const logException = (error, options = {}) => {
    logErrorToSentry(error, options);
};

/**
 * Log error message (for string messages)
 * @param {string} message - Error message
 * @param {Object} options - Logging options
 */
export const logErrorMessage = (message, options = {}) => {
    logErrorToSentry(message, options);
};

